import { FormGroup, FormGroupFeedback, IFormGroupProps, Intent } from '@pinpointhq/thumbtack';
import { useField } from 'formik';
import * as React from 'react';
import ReactIntlTelInput from 'react-intl-tel-input';
import { isoCountries } from '../../../iso2_countries';

interface ICountry {
  areaCodes: any;
  dialCode: string;
  iso2: string;
  name: string;
  priority: number;
}

export interface ITextFieldProps {
  children?: React.ReactNode;
  defaultIso2?: string;
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  placeholder?: string;
}

export default function PhoneNumberField<T extends string>({
  children,
  defaultIso2,
  name,
  formGroupProps,
  placeholder,
}: ITextFieldProps) {
  const [field, meta, helpers] = useField(name);
  const [isoField, isoMeta, isoHelpers] = useField(`${name}Iso2`);
  const hasError = meta.touched && meta.error;

  let detectedIso;
  // I'm worried about this breaking and stopping people submitting the form so using a dirty try catch to make sure
  // it doesn't brick the component
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    detectedIso = isoCountries[timeZone];
  } catch {
    detectedIso = null;
  }

  React.useEffect(() => {
    isoHelpers.setValue(isoField.value || defaultIso2 || detectedIso || 'GB');
  }, []);

  const handleCountryChange = (event: React.SyntheticEvent, country: ICountry) =>
    isoHelpers.setValue(country.iso2.toUpperCase());

  const handlePhoneChange = (valid, phone) => helpers.setValue(phone);

  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      {children}
      <ReactIntlTelInput
        value={field.value || ''}
        onPhoneNumberChange={handlePhoneChange}
        autoHideDialCode={false}
        defaultCountry={(isoField.value || defaultIso2 || detectedIso || 'GB').toLowerCase()}
        fieldName={name}
        placeholder={placeholder || 'Phone Number'}
        format={false}
        onSelectFlag={handleCountryChange}
        separateDialCode={false}
        style={{ display: 'block' }}
        id={name}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}
